import { FC, useMemo } from 'react'
import { When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { GuildScore } from '@/atoms/GuildScore'
import { TitleAndSubtitleDisplay } from '@/atoms/TitleAndSubtitleDisplay'
import { slugs } from '@/constants'
import { useExperimentContext } from '@/experimentation'
import { PortraitRowCollection } from '@/molecules/PortraitRowCollection'
import {
  buildLinkTabModel,
  buildLinkTabUrl,
  buildTabSubtitle,
  ComingSoonNode,
  shortenMonthInHeader,
} from '@/services/ComingSoonService'
import { useTranslate } from '@/utils/translate/translate-client'
import { LargePortraitLink } from '../CollectionDisplay/LargePortraitLink'

interface ComingSoonCollectionProps {
  title: string
  ComingSoonTabCollection?: ComingSoonNode[]
  testId?: string
  layout?: 'portrait-grid' | 'portrait-row'
}

export const ComingSoonCollection: FC<ComingSoonCollectionProps> = ({
  title,
  ComingSoonTabCollection,
  testId,
  layout = 'portrait-row',
}) => {
  const { t } = useTranslate('watch')
  const { getFeatureValue } = useExperimentContext()
  const comingSoonTheatricalTitleVariation = getFeatureValue(
    'coming-soon-theatrical-rail',
    'upcoming-theatrical-releases',
  )
  const comingSoonTitleVariation = getFeatureValue('coming-soon-header', 'coming-soon')

  const comingSoonTitle = useMemo(() => {
    if (comingSoonTitleVariation === 'coming-soon') {
      return title
    }
    if (comingSoonTitleVariation === 'coming-soon-angel') {
      return t('comingSoonAngel', 'Coming Soon to Angel')
    }
    if (comingSoonTitleVariation === 'coming-angel') {
      return t('comingAngel', 'Coming to Angel')
    }
    if (comingSoonTitleVariation === 'streaming-soon-angel') {
      return t('streamingSoonAngel', 'Streaming Soon on Angel')
    }

    return title
  }, [comingSoonTitleVariation, t, title])

  const comingSoonTheatricalTitle = useMemo(() => {
    if (comingSoonTheatricalTitleVariation === 'upcoming-theatrical-releases') {
      return t('upcomingTheatricalReleases', 'Upcoming Theatrical Releases')
    }
    if (comingSoonTheatricalTitleVariation === 'support-angel-theaters') {
      return t('supportAngelTheaters', 'Support Angel in Theaters')
    }
    if (comingSoonTheatricalTitleVariation === 'coming-theaters') {
      return t('comingTheaters', 'Coming to Theaters')
    }
    if (comingSoonTheatricalTitleVariation === 'angel-theaters') {
      return t('angelTheaters', 'Angel in Theaters')
    }

    return t('upcomingTheatricalReleases', 'Upcoming Theatrical Releases')
  }, [comingSoonTheatricalTitleVariation, t])

  const canDisplay = ComingSoonTabCollection && ComingSoonTabCollection?.length > 0
  const plannedTheatricalReleases = new Set([
    slugs.bull,
    slugs.fablehaven,
    slugs.fellowship,
    slugs.jacob,
    slugs.oldManInTheDunes,
    slugs.soloMio,
    slugs.strangersCase,
    slugs.youngWashington,
    slugs.zeroAd,
  ])

  return (
    <>
      <When condition={canDisplay && layout === 'portrait-row'}>
        <LazyHydrate whenVisible>
          <div className="flex flex-col gap-y-12">
            <PortraitRowCollection
              testId={testId}
              title={comingSoonTitle}
              slidesToScroll={6}
              slidesToShow={6}
              haltLazyLoad
            >
              {ComingSoonTabCollection?.map((item) => {
                if (!item) return null

                if (
                  item?.watchable?.__typename !== 'ContentMovie' &&
                  item?.watchable?.__typename !== 'ContentSeries' &&
                  item?.watchable?.__typename !== 'ContentSeason'
                ) {
                  return null
                }

                if (
                  item?.focus?.__typename === 'TheatricalTicketsSaleFocus' ||
                  (item?.project?.slug && plannedTheatricalReleases.has(item?.project?.slug))
                ) {
                  return null
                }

                const linkModel = buildLinkTabModel(item, true)
                if (!linkModel) return null

                const title = item.title ?? item?.watchable?.name ?? item?.project?.name
                const subTitle = buildTabSubtitle(item, t)

                const header = item.header && shortenMonthInHeader(item.header)

                return (
                  <LazyHydrate key={item?.id} whenVisible>
                    <>
                      <div className="mb-2 mr-4">
                        <LargePortraitLink link={{ ...linkModel, linkUrl: buildLinkTabUrl(item) }} />
                      </div>
                      <TitleAndSubtitleDisplay
                        className="mr-4"
                        title={title || undefined}
                        subtitle={header ? `${header}` : subTitle}
                      />
                      <When condition={item?.project?.highestScore}>
                        <GuildScore guildScore={item.project.highestScore} />
                      </When>
                    </>
                  </LazyHydrate>
                )
              })}
            </PortraitRowCollection>
            <PortraitRowCollection
              testId={testId}
              title={comingSoonTheatricalTitle}
              slidesToScroll={6}
              slidesToShow={6}
              haltLazyLoad
            >
              {ComingSoonTabCollection?.map((item) => {
                if (!item) return null

                if (
                  item?.watchable?.__typename !== 'ContentMovie' &&
                  item?.watchable?.__typename !== 'ContentSeries' &&
                  item?.watchable?.__typename !== 'ContentSeason'
                ) {
                  return null
                }

                if (
                  item?.focus?.__typename !== 'TheatricalTicketsSaleFocus' &&
                  item?.project?.slug &&
                  !plannedTheatricalReleases.has(item?.project?.slug)
                ) {
                  return null
                }

                const linkModel = buildLinkTabModel(item, true)
                if (!linkModel) return null

                const title = item.title ?? item?.watchable?.name ?? item?.project?.name
                const subTitle = buildTabSubtitle(item, t)

                const header = item.header && shortenMonthInHeader(item.header)

                return (
                  <LazyHydrate key={item?.id} whenVisible>
                    <>
                      <div className="mb-2 mr-4">
                        <LargePortraitLink link={{ ...linkModel, linkUrl: buildLinkTabUrl(item) }} />
                      </div>
                      <TitleAndSubtitleDisplay
                        className="mr-4"
                        title={title || undefined}
                        subtitle={header ? `${header}` : subTitle}
                      />
                      <When condition={item?.project?.highestScore}>
                        <GuildScore guildScore={item.project.highestScore} />
                      </When>
                    </>
                  </LazyHydrate>
                )
              })}
            </PortraitRowCollection>
          </div>
        </LazyHydrate>
      </When>
    </>
  )
}
